import { useCallback, useEffect, useState } from 'react';

import { ApiFailure, request } from '../../shared/api';
import { EmptyState, ListSkeleton, renderFailure } from '../../shared/states';
import { useLoad } from '../../shared/use-load';
import { usePage } from '../../shared/use-page';
import { openLink } from '../../telegram/webapp';

/**
 * Кошелёк в Mini App (T16.7, `05_API.md` §11.1).
 *
 * Открывается из строки профиля: остаток там показан всегда, но
 * пополнить оттуда нельзя — сумма требует ввода, а ввод требует экрана.
 *
 * ## Пополнение — тот же платёж, что и подписка
 *
 * `POST /v1/payments/checkout` с назначением `wallet_topup` заводит
 * счёт у той же кассы и той же записью в `payments` (`05_API.md`
 * §11.1): второй платёжный путь развёл бы подпись вебхука и защиту от
 * двойного зачисления по двум местам, а такие места расходятся всегда.
 *
 * Деньги зачисляет вебхук, а не этот экран: считать пополнение
 * состоявшимся по возвращению человека значило бы показать остаток,
 * которого нет.
 *
 * ## Ключ идемпотентности живёт при сумме, а не при экране
 *
 * На экране оплаты подписки ключ один на открытие: сумма там одна.
 * Здесь сумму выбирают, и повтор с прежним ключом вернул бы **прежний
 * счёт** — человек ввёл бы пять тысяч, а заплатил тысячу, которую
 * выбрал минуту назад. Поэтому ключ перевыпускается при смене суммы и
 * сохраняется, пока сумма та же: повторное нажатие «Пополнить» обязано
 * открыть тот же счёт, а не выставить второй.
 *
 * ## Успех виден по остатку, а не по слову кассы
 *
 * Вернувшись, экран сам спрашивает `/v1/wallet` и сравнивает остаток с
 * тем, что был до ухода. Это единственная проверка, которой можно
 * верить: касса отвечает нам, а зачисление делает вебхук, и между ними
 * проходят секунды.
 */

interface Snapshot {
  readonly balance_minor: string;
  readonly currency: string;
  readonly free_ai_calls: number;
  readonly free_ai_calls_left: number;
  /**
   * Границы пополнения, в копейках.
   *
   * Приходят с сервера, а не записаны здесь: экрану они нужны до
   * запроса — подсказать предел и не пускать заведомо отказную сумму, —
   * а своя копия разошлась бы с сервером в первый же раз, когда границу
   * подвинут, и человек получил бы отказ на сумму, которую экран принял.
   */
  readonly topup_min_minor: number;
  readonly topup_max_minor: number;
}

interface Transaction {
  readonly id: string;
  readonly amount_minor: string;
  readonly currency: string;
  readonly type: string;
  readonly status: string;
  readonly description: string | null;
  readonly created_at: string;
}

interface Checkout {
  readonly pay_url: string;
  readonly amount_minor: string | number;
}

/** Сколько операций показываем за раз. */
const PAGE = 20;

/**
 * Готовые суммы.
 *
 * Не для экономии нажатий: пустое поле ввода не подсказывает порядок
 * величины, и человек, не знающий, сколько стоит звонок, вводит либо
 * сто рублей, либо ничего.
 */
const PRESETS: readonly number[] = [500, 1000, 2000, 5000];

/** Виды операций словами. Коды те же, что в `wallet_transactions_type_check`. */
const KINDS: Record<string, string> = {
  topup: 'Пополнение',
  ai_call_charge: 'Звонок продавцу',
  ai_call_refund: 'Возврат за звонок',
  bonus: 'Бонус',
  referral_payout: 'Реферальное вознаграждение',
  admin_adjustment: 'Корректировка',
};

function kind(type: string): string {
  // Незнакомый вид называется общим словом: типы заводятся миграцией
  // раньше, чем попадают сюда, и пустая строка выглядела бы поломкой
  return KINDS[type] ?? 'Операция';
}

/**
 * Сумма в рублях.
 *
 * Копейки переводятся здесь, а не на сервере: деньги обязаны ходить
 * целым числом, иначе дробная арифметика однажды потеряет копейку, — а
 * показывать человеку всё равно надо рубли.
 */
function money(minor: string, currency: string): string {
  const major = Number(minor) / 100;
  const suffix = currency === 'RUB' ? ' ₽' : ` ${currency}`;

  return `${major.toLocaleString('ru-RU', { minimumFractionDigits: 2 })}${suffix}`;
}

/** Со знаком: списание и зачисление в одном списке иначе неразличимы. */
function signed(minor: string, currency: string): string {
  const negative = minor.startsWith('-');
  const shown = money(negative ? minor.slice(1) : minor, currency);

  return negative ? `− ${shown}` : `+ ${shown}`;
}

/** Целые рубли для подсказок: копейки в границе только мешают читать. */
function rubles(minor: number): string {
  return Math.round(minor / 100).toLocaleString('ru-RU');
}

function day(at: string): string {
  return new Date(at).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Введённое — в копейки.
 *
 * Пробелы и запятая разрешены: «5 000» и «1,5» человек напишет скорее,
 * чем «5000» и «1.5». Отказ отличается от нуля намеренно — ноль прошёл
 * бы дальше как допустимая сумма.
 */
function parseAmount(text: string): number | null {
  const cleaned = text.replace(/\s/g, '').replace(',', '.');

  if (cleaned === '' || !/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;

  // Округление до копейки: 1.005 в двоичной дроби чуть меньше себя, и
  // без него сумма ушла бы на копейку вниз
  return Math.round(Number(cleaned) * 100);
}

type Stage =
  | { readonly kind: 'idle' }
  | { readonly kind: 'creating' }
  | { readonly kind: 'opened' }
  | { readonly kind: 'checking' }
  | { readonly kind: 'done' }
  | { readonly kind: 'failed'; readonly hint: string };

/** Выставленный счёт: ключ вместе с суммой, за которую он выдан. */
interface Order {
  readonly key: string;
  readonly amountMinor: number;
}

export function WalletScreen({ onBack }: { onBack: () => void }): React.JSX.Element {
  const [state, retry] = useLoad<Snapshot>(useCallback(() => request<Snapshot>('/v1/wallet'), []));
  const history = usePage<Transaction>('/v1/wallet/transactions', PAGE);
  const [amount, setAmount] = useState('');
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [order, setOrder] = useState<Order | null>(null);
  // Остаток на момент ухода на оплату: с ним сравнивается новый
  const [before, setBefore] = useState<string | null>(null);

  const minor = parseAmount(amount);
  const snapshot = state.kind === 'ready' ? state.value : null;
  const valid =
    minor !== null &&
    snapshot !== null &&
    minor >= snapshot.topup_min_minor &&
    minor <= snapshot.topup_max_minor;
  const balance = snapshot?.balance_minor ?? null;

  const pay = useCallback(() => {
    if (minor === null || !valid) return;

    setStage({ kind: 'creating' });
    setBefore(balance);

    // Тот же ключ, пока сумма не менялась: повтор обязан вернуть тот же
    // счёт. Сменилась — новый ключ, иначе касса покажет прежнюю сумму
    const issued: Order =
      order !== null && order.amountMinor === minor
        ? order
        : { key: crypto.randomUUID(), amountMinor: minor };

    setOrder(issued);

    request<Checkout>('/v1/payments/checkout', {
      method: 'POST',
      body: { purpose: 'wallet_topup', amount_minor: minor },
      headers: { 'idempotency-key': issued.key },
    })
      .then((checkout) => {
        setStage({ kind: 'opened' });
        // Через Telegram: внутри Mini App обычное окно блокируется, и
        // страница оплаты молча не открылась бы
        openLink(checkout.pay_url);
      })
      .catch((failure: unknown) => {
        setStage({
          kind: 'failed',
          hint:
            failure instanceof ApiFailure
              ? failure.message
              : 'Сервис не ответил. Это бывает; попробуйте ещё раз.',
        });
      });
  }, [balance, minor, order, valid]);

  /**
   * Спрашивает сервер, вырос ли остаток.
   *
   * Правда о деньгах одна — та, что на сервере. Возвращение человека из
   * браузера ничего о зачислении не говорит: он мог закрыть страницу
   * кассы, не заплатив.
   */
  const arrived = useCallback(async (): Promise<boolean> => {
    if (before === null) return false;

    try {
      const now = await request<Snapshot>('/v1/wallet');

      return BigInt(now.balance_minor) > BigInt(before);
    } catch {
      // Сеть подвела — это не «не зачислено», а «не знаем». Экран
      // остаётся прежним, и человек может спросить ещё раз
      return false;
    }
  }, [before]);

  const settle = useCallback(() => {
    setStage({ kind: 'done' });
    setOrder(null);
    setAmount('');
    setBefore(null);
    retry();
    history.reload();
  }, [history, retry]);

  /** Проверка по кнопке: видно, что она идёт. */
  const check = useCallback(() => {
    setStage({ kind: 'checking' });
    void arrived().then((yes) => {
      if (yes) settle();
      else setStage({ kind: 'opened' });
    });
  }, [arrived, settle]);

  // Возвращение в приложение — сигнал, что человек со страницей оплаты
  // закончил. Спрашиваем сервер сами: событий об оплате Telegram не
  // присылает, а деньги к этому времени уже зачислены вебхуком
  useEffect(() => {
    if (stage.kind !== 'opened') return undefined;

    // Тихо: показывать «Проверяю…» на каждое возвращение к экрану
    // значило бы мигать состоянием там, где человек ничего не просил
    const again = (): void => {
      if (document.visibilityState !== 'visible') return;

      void arrived().then((yes) => {
        if (yes) settle();
      });
    };

    document.addEventListener('visibilitychange', again);
    window.addEventListener('focus', again);

    // Первые секунды касса может ещё не прислать подтверждение: три
    // попытки с паузой закрывают этот разрыв, дальше остаётся кнопка
    const timers = [2000, 6000, 15_000].map((delayMs) => setTimeout(again, delayMs));

    return () => {
      document.removeEventListener('visibilitychange', again);
      window.removeEventListener('focus', again);
      timers.forEach(clearTimeout);
    };
  }, [arrived, settle, stage.kind]);

  if (state.kind === 'loading') return <ListSkeleton rows={3} />;

  if (state.kind === 'failed') {
    return <>{renderFailure(state.error, { onRetry: retry, onUpgrade: retry })}</>;
  }

  const wallet = state.value;
  const busy = stage.kind === 'creating';

  return (
    <>
      <button type="button" className="back" onClick={onBack}>
        ← Назад
      </button>

      <div className="card offer">
        <div className="offer__head">
          <span className="offer__name">Остаток</span>
          <span className="offer__price">{money(wallet.balance_minor, wallet.currency)}</span>
        </div>

        {/*
          Бесплатные звонки стоят рядом с остатком не для красоты: перед
          звонком важно не «сколько денег», а «спишут ли вообще»
        */}
        <p className="offer__note">
          {wallet.free_ai_calls_left > 0
            ? `Бесплатных звонков по тарифу: ${String(wallet.free_ai_calls_left)} из ${String(wallet.free_ai_calls)}`
            : 'Бесплатные звонки по тарифу израсходованы'}
        </p>
      </div>

      <div className="card subscribe">
        <p className="subscribe__title">Пополнить кошелёк</p>

        <div className="chips">
          {PRESETS.map((rub) => (
            <button
              key={rub}
              type="button"
              className="chip"
              aria-pressed={minor === rub * 100}
              onClick={() => {
                setAmount(String(rub));
                // Прежний счёт больше не относится к делу: сумма
                // сменилась, и старая ссылка увела бы не туда
                setStage({ kind: 'idle' });
              }}
            >
              {rub.toLocaleString('ru-RU')} ₽
            </button>
          ))}
        </div>

        <div className="field">
          <label className="field__title" htmlFor="topup-amount">
            Сумма, ₽
          </label>
          <input
            id="topup-amount"
            className="input"
            // Числовая клавиатура, но текстовое поле: `type="number"` в
            // мобильных браузерах теряет введённое при прокрутке и не
            // пускает пробелы, которые человек ставит в тысячах
            type="text"
            inputMode="decimal"
            value={amount}
            placeholder={`от ${rubles(wallet.topup_min_minor)} до ${rubles(wallet.topup_max_minor)}`}
            onChange={(event) => {
              setAmount(event.target.value);
              setStage({ kind: 'idle' });
            }}
          />
        </div>

        {amount !== '' && !valid ? (
          <p className="subscribe__warning">
            {minor === null
              ? 'Введите сумму цифрами, например 1500'
              : `Сумма должна быть от ${rubles(wallet.topup_min_minor)} до ${rubles(wallet.topup_max_minor)} ₽`}
          </p>
        ) : null}

        {stage.kind === 'failed' ? <p className="subscribe__warning">{stage.hint}</p> : null}

        {stage.kind === 'done' ? (
          <p className="subscribe__hint">Деньги зачислены. Остаток обновлён.</p>
        ) : null}

        {stage.kind === 'opened' || stage.kind === 'checking' ? (
          <>
            <p className="subscribe__hint">
              Страница оплаты открыта в браузере. Оплатите и вернитесь сюда — деньги зачислятся
              сами, обычно за несколько секунд.
            </p>
            <div className="subscribe__actions">
              <button
                type="button"
                className="button button--accent"
                disabled={stage.kind === 'checking'}
                onClick={check}
              >
                {stage.kind === 'checking' ? 'Проверяю…' : 'Проверить оплату'}
              </button>
              <button type="button" className="button" onClick={pay}>
                Открыть снова
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            className="button button--accent"
            disabled={!valid || busy}
            onClick={pay}
          >
            {busy ? 'Готовлю счёт…' : 'Пополнить'}
          </button>
        )}

        {/*
          Оплата уходит на страницу провайдера: карта и её данные через
          нас не проходят и у нас не хранятся (`08_SECURITY.md` §8), и
          сказать об этом стоит там, где человек решает платить
        */}
        <p className="offer__note">
          Оплата на стороне платёжного сервиса. Данные карты к нам не попадают.
        </p>
      </div>

      <h2 className="screen__subtitle">История операций</h2>

      {history.state === 'loading' ? <ListSkeleton rows={3} /> : null}

      {history.error !== null && history.items.length === 0
        ? renderFailure(history.error, { onRetry: history.reload, onUpgrade: history.reload })
        : null}

      {history.state !== 'loading' && history.error === null && history.items.length === 0 ? (
        <EmptyState
          title="Операций пока нет"
          hint="Здесь появятся пополнения и списания за звонки продавцам."
        />
      ) : null}

      {history.items.map((one) => (
        <div className="card menu menu--idle" key={one.id}>
          <span className="menu__title">
            {one.description ?? kind(one.type)}
            {/*
              Отменённое остаётся в списке: пропавшая строка выглядит
              как потерянные деньги, и человек идёт в поддержку
            */}
            {one.status === 'cancelled' ? ' · отменено' : ''}
            {one.status === 'pending' ? ' · в обработке' : ''}
          </span>
          <span className="menu__note">
            {signed(one.amount_minor, one.currency)} · {day(one.created_at)}
          </span>
        </div>
      ))}

      {history.hasMore ? (
        <button
          type="button"
          className="button"
          disabled={history.state === 'more'}
          onClick={history.more}
        >
          {history.state === 'more' ? 'Загружаю…' : 'Показать ещё'}
        </button>
      ) : null}
    </>
  );
}
